import { inject, Injectable } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { filter, isEqual, isUndefined, orderBy, values, without } from 'lodash';
import { shareReplay } from 'rxjs';
import { distinctUntilChanged, map, pairwise } from 'rxjs/operators';
import { ObjectState } from '../../../cross-team-shared/annotation/shared/state/object-state';
import { keys } from '../../../cross-team-shared/misc-utils/keys';
import { Project } from '../../../team-apis/otterdam/project';
import { getShapesFromShapeInfo } from '../../h3ml/state/task-workspace-state.utils';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { getDefaultObjectTransientInfo } from '../utils/object-utils';
import { ObjectScopingService } from './object-scoping.service';
import { ObjectSortingService } from './object-sorting.service';

export const objectSequencesFeaturesErrorCause = 'disabled';

@Injectable()
export class ObjectService {
  private readonly objectSortingService = inject(ObjectSortingService);
  private readonly objectScopingService = inject(ObjectScopingService);
  private readonly taskWorkspaceStore = inject(TaskWorkspaceStore);

  objectIdsWithParentIdChanged$ = this.taskWorkspaceStore('objects').$.pipe(
    pairwise(),
    map(([objectsBefore, objectsAfter]) =>
      filter(
        objectsAfter,
        (object, objectId) =>
          // fix me if you change this code

          !objectsBefore[+objectId] ||
          // @ts-ignore -- fix me
          objectsBefore[+objectId].parentId !== object.parentId,
        // @ts-ignore -- fix me
      ).map((object) => object.index),
    ),
    distinctUntilChanged<number[]>(isEqual),
    shareReplay({ refCount: true }),
  );

  createObject(initialValues: Partial<ObjectState> = {}) {
    const parentObjectId = initialValues.parentId;
    this.ensureObjectCreationEnabled();
    if (isDefined(parentObjectId)) {
      this.ensureObjectNestingEnabled();
    }

    const index = this.getNextObjectId();
    const object = {
      tags: {},
      dynamic_tags: {},
      ...initialValues,
      // all other properties must be initialized with their default values
      index,
      childObjectIds: [],
      childShapeIds: [],
      parentId: parentObjectId,
      sortIndex: this.objectSortingService.getTopSortIndex(parentObjectId),
    };

    this.taskWorkspaceStore('objects')(index).set(object);
    // real transient info values will be calculated by annotation transient
    // info service
    this.taskWorkspaceStore('objectTransientInfo')(index).set(
      getDefaultObjectTransientInfo(),
    );

    if (isDefined(parentObjectId)) {
      this.updateChildObjectIds(parentObjectId, index);
    }

    return object;
  }

  nestSelectedObjects(targetObjectId: number | undefined) {
    this.ensureObjectNestingEnabled();

    const { objectSelection, scopedObjectId } = this.taskWorkspaceStore.state();
    const selectedObjectIds = keys(objectSelection);

    this.taskWorkspaceStore.batch(() => {
      selectedObjectIds.forEach((objectId) => {
        // @ts-ignore -- fix me
        this.taskWorkspaceStore('objects')(objectId)('parentId').set(
          // @ts-ignore -- fix me
          targetObjectId,
        );
      });

      if (isDefined(scopedObjectId) && this.isObjectEmpty(scopedObjectId)) {
        this.deleteObject(scopedObjectId);
      }

      if (isDefined(targetObjectId)) {
        this.updateChildObjectIds(targetObjectId, ...selectedObjectIds);
      }
    });
  }

  removeShapeFromObjects(
    affectedObjectIds: number[],
    shapeToDeleteIds: number[],
  ) {
    const { objects } = this.taskWorkspaceStore.state();
    const objectToChecks = new Set<number>(
      orderBy(affectedObjectIds, [(id) => this.getNestingLevel(id)], ['desc']),
    );
    const emptyObjectToDeleteIds: number[] = [];

    for (const objectId of objectToChecks) {
      const object = objects[objectId]!;
      const newObject = this.removeChildren(
        object,
        shapeToDeleteIds,
        emptyObjectToDeleteIds,
      );

      if (this.isEmpty(newObject)) {
        this.ensureObjectDeletionEnabled();
        emptyObjectToDeleteIds.push(objectId);
        if (isDefined(object.parentId)) {
          objectToChecks.add(object.parentId);
        }
      } else {
        this.taskWorkspaceStore
          .getObjectStore(objectId)('childShapeIds')
          .set(newObject.childShapeIds);
      }
    }

    emptyObjectToDeleteIds.forEach((objectId) => this.deleteObject(objectId));
  }

  getNextObjectId() {
    const idStore = this.taskWorkspaceStore('nextObjectId');
    const nextId = idStore.state();
    idStore.set(nextId + 1);
    return nextId;
  }

  private deleteObject(objectId: number) {
    // this may lead to empty objects, issue has been acknowledged but
    // not a priority to fix since it is a very corner case (we don't)
    // expect to have nest change enabled but deletion disabled
    this.ensureObjectDeletionEnabled();

    const { objects, scopedObjectId, objectSelection } =
      this.taskWorkspaceStore.state();
    const object = objects[objectId];
    if (isUndefined(object)) {
      return;
    }

    this.taskWorkspaceStore.batch(() => {
      if (scopedObjectId === object.index) {
        this.objectScopingService.scopeObject(object.parentId, 'delete_shape');
      }

      this.taskWorkspaceStore('objects')(objectId).delete();
      this.taskWorkspaceStore('objectTransientInfo')(objectId).delete();

      if (objectSelection[objectId]) {
        this.taskWorkspaceStore('objectSelection').mutateUsing(
          (selection) => delete selection[objectId],
        );
      }

      if (isDefined(object.parentId)) {
        if (this.isObjectEmpty(object.parentId)) {
          this.deleteObject(object.parentId);
        } else {
          this.removeObjectFromParent(object);
        }
      }
    });
  }

  private updateChildObjectIds(parentObjectId: number, ...objectIds: number[]) {
    const parentObjectStore =
      this.taskWorkspaceStore.getObjectStore(parentObjectId);
    parentObjectStore('childObjectIds').mutateUsing((childObjectIds) =>
      childObjectIds.push(...objectIds),
    );
  }

  private getNestingLevel(objectId: number) {
    const { objects } = this.taskWorkspaceStore.state();
    let nestingLevel = 0;
    let parentId = objects[objectId]?.parentId;
    while (isDefined(parentId)) {
      parentId = objects[parentId]?.parentId;
      nestingLevel += 1;
    }
    return nestingLevel;
  }

  private isObjectEmpty(objectId: number) {
    const { objects } = this.taskWorkspaceStore.state();
    // @ts-ignore -- fix me
    if (values(objects).some((object) => object.parentId === objectId)) {
      return false;
    }

    const allShapes = getShapesFromShapeInfo(
      this.taskWorkspaceStore.state().outputAnnotationInfo,
    );
    // @ts-ignore -- fix me
    return !allShapes.some((shape) => shape.parentId === objectId);
  }

  private removeChildren(
    object: ObjectState,
    shapeIdToRemoves: number[],
    objectIdToRemoves: number[],
  ): ObjectState {
    const childShapeIds = object.childShapeIds.filter(
      (shapeId) => !shapeIdToRemoves.includes(shapeId),
    );
    const childObjectIds = object.childObjectIds.filter(
      (id) => !objectIdToRemoves.includes(id),
    );

    return {
      ...object,
      childShapeIds,
      childObjectIds,
    };
  }

  private isEmpty(object: ObjectState) {
    return (
      object.childShapeIds.length === 0 && object.childObjectIds.length === 0
    );
  }

  private ensureObjectDeletionEnabled() {
    if (!isObjectDeletionEnabled(this.taskWorkspaceStore.state())) {
      throw new Error('Object deletion is disabled', {
        cause: objectSequencesFeaturesErrorCause,
      });
    }
  }

  private ensureObjectCreationEnabled() {
    if (!isObjectCreationEnabled(this.taskWorkspaceStore.state())) {
      throw new Error('Object creation is disabled', {
        cause: objectSequencesFeaturesErrorCause,
      });
    }
  }

  private ensureObjectNestingEnabled() {
    if (!isObjectNestingEnabled(this.taskWorkspaceStore.state())) {
      throw new Error('Object nesting change is disabled', {
        cause: objectSequencesFeaturesErrorCause,
      });
    }
  }

  private removeObjectFromParent(objectState: ObjectState) {
    assert(
      isDefined(objectState.parentId),
      'Object should have a defined parent id',
    );
    const parentObjectStore = this.taskWorkspaceStore.getObjectStore(
      objectState.parentId,
    );

    parentObjectStore('childObjectIds').setUsing((childObjectIds) =>
      without(childObjectIds, objectState.index),
    );
  }
}

function isObjectDeletionEnabled(args: {
  project: Project;
  readOnly: boolean;
}) {
  const { project, readOnly } = args;
  return !project.object_deletion_off && !readOnly;
}

function isObjectCreationEnabled(args: {
  project: Project;
  readOnly: boolean;
}) {
  const { project, readOnly } = args;
  return !project.object_creation_off && !readOnly;
}

function isObjectNestingEnabled(args: { project: Project; readOnly: boolean }) {
  const { project, readOnly } = args;
  return !project.object_nesting_change_off && !readOnly;
}
